import styled from '@emotion/styled';
import React, { FC } from 'react';
import { ExpandMore, Functions } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';
import katex from 'katex';

export const LatexHelp: FC = () => {
  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMore />} id="panel-header" aria-controls="panel-content">
        <span
          dangerouslySetInnerHTML={{
            __html: katex.renderToString('\\LaTeX'),
          }}></span>
        -formler
      </AccordionSummary>
      <AccordionDetails>
        <SectionHeader style={{ marginTop: 0 }}>Generelle regler for formler</SectionHeader>
        Formler skal lages i{' '}
        <span
          dangerouslySetInnerHTML={{
            __html: katex.renderToString('\\LaTeX'),
          }}></span>
        -format.
        <br />
        Fonten skal være i standard LaTeX-font, men uten seriffer.
        <br /> Følg{' '}
        <a
          href="https://sintef.sharepoint.com/sites/simpli/Manage/Procedures/Forms/AllItems.aspx?id=/sites/simpli/Manage/Procedures/Skriveregler%202024.pdf&parent=/sites/simpli/Manage/Procedures"
          target="_blank">
          skrivereglene
        </a>{' '}
        for BFS om formler:
        <ul>
          <li>Variable symboler skal i hovedsak være i kursiv tekst</li>
          <li>Konstanter, enheter og andre ikke-variable skal være uten kursiv</li>
          <li>Kontroller symboler mot evt. gjeldende standard</li>
        </ul>
        Følg standardoppsettet i LaTeX så langt som mulig, og gjør tilpasninger kun der det er nødvendig.
        <SectionHeader>Hjelp til å komme i gang</SectionHeader>
        For å skrive formler i LaTeX-format trykk på <Functions style={{ verticalAlign: 'text-bottom' }} />
        -ikonet i verktøylinjen. Formelen skrives mellom $-tegnene.
        <LatexExample latex={'E = mc^2'} />
        Se{' '}
        <a href="https://katex.org/docs/supported" target="_blank">
          KaTeX
        </a>{' '}
        for en full oversikt over hvilke funksjoner man kan bruke.
        <br />
        For hjelp til å skrive mer avanserte formler, kan man bruke denne{' '}
        <a href="https://latexeditor.lagrida.com/" target="_blank">
          online LaTeX editoren
        </a>
        .<SectionHeader>Hvordan få riktig font</SectionHeader>
        For å få font uten seriffer bruk <StyledFormula>{'\\mathsfit{}'}</StyledFormula> (kursiv) eller
        <StyledFormula>{'\\mathsf{}'}</StyledFormula> (normal).
        <LatexExample latex={'\\mathsfit{E} = \\mathsfit{mc}^\\mathsf{2}'} />
        <LatexExample latex={'\\mathsf{Fe \\to Fe^{2+}+ 2e^-}'} />
        Dersom man har ren tekst uten understrek, symboler etc kan man også bruke <StyledFormula>{'\\textsf{}'}</StyledFormula>
        <ExampleHeader>Tekst i Sans Serif</ExampleHeader>
        <LatexExample latex={'\\textsf{Dette er en tekst}'} />
        <ExampleHeader>Tekst i kursiv Sans Serif</ExampleHeader>
        <LatexExample latex={'\\textsf{\\textit{Dette er en tekst}}'} />
        <SectionHeader>Hvordan få riktig avtand mellom formel og enhet</SectionHeader>
        Enheter (i parantes) legges inn samtidig med formelen. Bruk <StyledFormula>\qquad</StyledFormula> etter formelen for å få riktig avstand på
        mellomrom.
        <LatexExample latex={'\\mathsfit{C_L = \\sqrt{\\frac{E}{\\rho}}} \\qquad \\textsf{(m/s)}'} />
        <SectionHeader>Tilpasse størrelse på formelen</SectionHeader>
        Når en formel blir høy, vil LaTeX ofte vise veldig små fonter. For å få riktig utseende må man legge inn{' '}
        <StyledFormula>\displaystyle</StyledFormula> i formelen.
        <ExampleHeader>Eksempel 1 uten display style</ExampleHeader>
        <LatexExample latex={'\\mathsfit{\\frac{x^\\mathsf{2}}{y}}'} />
        <ExampleHeader>Eksempel 2 med display style</ExampleHeader>
        <LatexExample latex={'\\displaystyle \\mathsfit{\\frac{x^\\mathsf{2}}{y}}'} />
        <ExampleHeader>Eksempel 3 uten display style</ExampleHeader>
        <LatexExample latex={'\\frac{\\mathsfit{\\sum_{i=\\mathsf{1}}^{n} p_i^\\mathsf{2}}}{\\mathsfit{n \\cdot p_0^\\mathsf{2}}}'} />
        <ExampleHeader>Eksempel 4 med display style på formelen og på summen</ExampleHeader>I mer kompliserte formler må man legge på{' '}
        <StyledFormula>\displaystyle</StyledFormula> flere steder for å oppnå ønsket uttrykk. Da må man også legge på{' '}
        <StyledFormula>\mathsfit</StyledFormula> på nytt for å få riktig stilsetting:
        <br />
        <LatexExample
          latex={'\\displaystyle \\frac{\\displaystyle \\mathsfit{\\sum_{i=\\mathsf{1}}^{n} p_i^\\mathsf{2}}}{\\mathsfit{n \\cdot p_0^\\mathsf{2}}}'}
        />
      </AccordionDetails>
    </Accordion>
  );
};

const BlockKatex = styled.div`
  margin-top: 10px;
`;

const LatexExample: FC<{ latex: string }> = ({ latex }) => {
  return (
    <Box sx={{ mt: 1, mb: 1, borderLeft: '3px solid', borderColor: 'action.focus', paddingLeft: 2 }}>
      <StyledFormula>{`$${latex}$`}</StyledFormula>
      <BlockKatex dangerouslySetInnerHTML={{ __html: katex.renderToString(latex) }}></BlockKatex>
    </Box>
  );
};

const SectionHeader: FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => {
  return (
    <Typography variant="h6" component="h3" marginTop={2} style={style}>
      {children}
    </Typography>
  );
};

const ExampleHeader = styled.div`
  font-weight: 500;
  margin-top: 10px;
  margin-bottom: 6px;
`;

const StyledFormula = styled.span<{ marginRight?: boolean }>`
  background: #e6edf0;
  color: #000;
  font-family: monospace;
  font-size: 0.8rem;
  padding: 0.2rem;
  ${({ marginRight }) => marginRight && 'margin-right: 10px;'}
`;
